import React, { useMemo, useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Upload, AlertTriangle } from "lucide-react";
import { FaRegCalendarAlt } from "react-icons/fa";
import Table1 from "../../../../components/ACS-Dashboard/ACS-Blocks/TableSecondary";

const pad = (n) => String(n).padStart(2, "0");
const todayLocalDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const defaultStartTime = "09:00";
const defaultEndTime = "17:00";

const makeDefaultRange = (baseDate) => ({
  startDate: baseDate || todayLocalDate(),
  endDate: baseDate || todayLocalDate(),
  startTime: defaultStartTime,
  endTime: defaultEndTime,
});

const DateInput = ({ value, onChange, min }) => {
  const inputRef = useRef(null);
  return (
    <div className="relative flex items-center">
      <FaRegCalendarAlt
        className="absolute left-2 text-[#1e2a74] w-4 h-4 cursor-pointer"
        onClick={() => {
          if (inputRef.current?.showPicker) {
            try {
              inputRef.current.showPicker();
            } catch (_) {
              /* ignore */
            }
          } else {
            inputRef.current?.focus();
          }
        }}
      />
      <input
        ref={inputRef}
        type="date"
        value={value}
        min={min}
        onChange={onChange}
        className="pl-7 pr-2 py-1.5 border border-gray-300 rounded-md w-full text-fluid-sm focus:border-[#1e2a74] focus:outline-none"
      />
    </div>
  );
};

const BulkSelectionModal = ({
  onClose,
  onConfirm,
  rows,
  selectedWarehouseName = "",
  selectedDate,
}) => {
  const [data] = useState(rows);
  const [ranges, setRanges] = useState({});
  const [reason, setReason] = useState("Maintenance");
  const [isSubmitting, setIsSubmitting] = useState(false);

  console.log("Warehouse", { selectedWarehouseName });

  const rowByKey = (k) =>
    (rows || []).find((r) => (r.dockId ?? r.dockName) === k);

  // selection
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const allKeys = useMemo(
    () => (data || []).map((r) => r.dockId ?? r.dockName).filter(Boolean),
    [data]
  );
  const allSelected =
    selectedKeys.size > 0 && selectedKeys.size === allKeys.length;
  const anySelected = selectedKeys.size > 0;

  const toggleAll = () => {
    setSelectedKeys((prev) =>
      prev.size === allKeys.length ? new Set() : new Set(allKeys)
    );
  };
  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // indeterminate for header checkbox
  const selectAllRef = useRef(null);
  useEffect(() => {
    if (selectAllRef.current)
      selectAllRef.current.indeterminate = !allSelected && anySelected;
  }, [allSelected, anySelected]);

  useEffect(() => {
    setRanges((prev) => {
      const next = { ...prev };
      (rows || []).forEach((r) => {
        const key = r.dockId ?? r.dockName;
        if (key && !next[key]) next[key] = makeDefaultRange(selectedDate);
      });
      return next;
    });
  }, [rows, selectedDate]);

  const isRowValid = (r) => {
    if (!r) return false;
    const dateOK = r.endDate >= r.startDate;
    const timeOK =
      r.endDate > r.startDate ||
      (r.endDate === r.startDate && r.endTime > r.startTime);
    return dateOK && timeOK;
  };

  const selectedKeysArr = useMemo(
    () => Array.from(selectedKeys),
    [selectedKeys]
  );

  const allSelectedRangesValid = selectedKeysArr.every((k) => {
    const r = ranges[k];
    if (!r) return false;
    const dateOK = r.endDate >= r.startDate;
    const timeOK =
      r.endDate > r.startDate ||
      (r.endDate === r.startDate && r.endTime > r.startTime);
    return dateOK && timeOK;
  });

  const invalidSelected = useMemo(() => {
    return selectedKeysArr.filter((k) => !isRowValid(ranges[k]));
  }, [selectedKeysArr, ranges]);

  const nameForKey = (k) => {
    const row = (rows || []).find((r) => (r.dockId ?? r.dockName) === k);
    return row?.dockName ?? String(k);
  };

  return (
    <motion.div
      className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center px-fluid-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-2xl shadow-xl w-full max-w-7xl p-fluid-4 space-y-fluid-4"
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
      >
        {/* Header */}
        <div className="flex flex-col items-center space-y-fluid-1.5">
          <div className="bg-custom-bg-blue rounded-full p-fluid-2 text-white">
            <Upload size={32} />
          </div>
          <h2 className="text-fluid-2xl font-semibold text-[#111827] text-center">
            Dock Closing Management
          </h2>
          <p className="text-gray-600 text-fluid-sm text-center">
            Select dock(s) to close for{" "}
            <strong>{selectedWarehouseName || "this warehouse"}</strong>.
          </p>
        </div>

        {invalidSelected.length > 0 && (
          <div
            className="rounded-md border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm"
            role="alert"
          >
            <div className="font-semibold mb-1">
              Fix the date/time for these dock(s):
            </div>
            <ul className="list-disc ml-5 space-y-0.5">
              {invalidSelected.slice(0, 5).map((k) => (
                <li key={k}>{nameForKey(k)}</li>
              ))}
            </ul>
            {invalidSelected.length > 5 && (
              <div className="mt-1">+{invalidSelected.length - 5} more…</div>
            )}
            <div className="mt-2">
              <span className="font-medium">Rules:</span> End date must be
              same/after start date, and if same day, end time must be after
              start time.
            </div>
          </div>
        )}

        {/* Warning */}
        <div className="bg-[#f8c1bc] border-l-4 border-[#b91c1c] px-fluid-2.5 py-fluid-2 rounded-md space-y-fluid-1">
          <div className="flex items-center text-[#111827] space-x-fluid-1">
            <AlertTriangle className="text-[#111827]" size={18} />
            <div className="text-fluid-base">
              <span className="font-bold">Warning</span>
              <span className="ml-fluid-0.5 font-medium">
                {" "}
                This will remove all of your appointments for the selected
                dates.
              </span>
            </div>
          </div>
          <p className="text-[#111827] text-fluid-sm pl-[1.75rem]">
            Carriers will have to place another appointment.
          </p>
        </div>

        {/* SCROLL CONTAINER (fix height) */}
            <div className="max-h-[420px] overflow-y-auto bulk-table">
             <Table1
                headers={[
                  <div
                    key="select-all"
                    className="flex items-center justify-center gap-2 whitespace-nowrap text-white"
                  >
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label="Select all docks"
                      className="h-4 w-4 cursor-pointer accent-[#1e2a74]"
                    />
                  </div>,
                  "Dock",
                  "Start Date",
                  "End Date",
                  "Start Time",
                  "End Time",
                ]}
                columnWidths={["8%", "22%", "20%", "20%", "15%", "15%"]}
                data={data || []}
                renderRow={(row) => {
                  const key = row.dockId ?? row.dockName;
                  const checked = selectedKeys.has(key);
                  const r = ranges[key] || makeDefaultRange(selectedDate);

                  return (
                    <>
                      {/* SELECT */}
                      <td
                        className={`px-fluid-3 py-fluid-2 text-center ${
                          checked ? "bg-blue-50" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOne(key)}
                          className="h-4 w-4 cursor-pointer accent-[#1e2a74]"
                        />
                      </td>

                      {/* DOCK NAME */}
                      <td
                        className={`px-fluid-3 py-fluid-2 text-fluid-sm text-[#030229] ${
                          checked ? "bg-blue-50" : ""
                        }`}
                      >
                        {row.dockName}
                      </td>

                      {/* START DATE */}
                      <td
                        className={`px-fluid-3 py-fluid-2 text-fluid-sm ${
                          checked ? "bg-blue-50" : ""
                        }`}
                      >
                        <DateInput
                          value={r.startDate}
                          onChange={(e) =>
                            setRanges((prev) => ({
                              ...prev,
                              [key]: { ...r, startDate: e.target.value },
                            }))
                          }
                        />
                      </td>

                      {/* END DATE */}
                      <td
                        className={`px-fluid-3 py-fluid-2 text-fluid-sm ${
                          checked ? "bg-blue-50" : ""
                        }`}
                      >
                        <DateInput
                          value={r.endDate}
                          min={r.startDate}
                          onChange={(e) =>
                            setRanges((prev) => ({
                              ...prev,
                              [key]: { ...r, endDate: e.target.value },
                            }))
                          }
                        />
                      </td>

                      {/* START TIME */}
                      <td
                        className={`px-fluid-3 py-fluid-2 text-fluid-sm ${
                          checked ? "bg-blue-50" : ""
                        }`}
                      >
                        <input
                          type="time"
                          value={r.startTime}
                          onChange={(e) =>
                            setRanges((prev) => ({
                              ...prev,
                              [key]: { ...r, startTime: e.target.value },
                            }))
                          }
                          className="border border-gray-300 rounded-md px-2 py-1.5 w-full text-fluid-sm focus:border-[#1e2a74] focus:outline-none"
                        />
                      </td>

                      {/* END TIME */}
                      <td
                        className={`px-fluid-3 py-fluid-2 text-fluid-sm ${
                          checked ? "bg-blue-50" : ""
                        }`}
                      >
                        <input
                          type="time"
                          value={r.endTime}
                          min={r.startDate === r.endDate ? r.startTime : undefined}
                          onChange={(e) =>
                            setRanges((prev) => ({
                              ...prev,
                              [key]: { ...r, endTime: e.target.value },
                            }))
                          }
                          className="border border-gray-300 rounded-md px-2 py-1.5 w-full text-fluid-sm focus:border-[#1e2a74] focus:outline-none"
                        />
                      </td>
                    </>
                  );
                }}
              />

            </div>
       
    


        <div className="flex items-center gap-3">
          <label className="text-sm text-[#111827] font-medium">Reason</label>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Maintenance"
            className="border rounded-md px-3 py-2 text-sm flex-1"
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-fluid-2">
          <div className="text-sm text-[#4b5563]">
            {anySelected
              ? `${selectedKeys.size} dock(s) selected`
              : "No docks selected"}
          </div>
          <div className="flex items-center gap-fluid-2">
            <button
              onClick={onClose}
              className="px-fluid-button-x py-fluid-button-y rounded-full border border-[#1f2d6b] text-[#1f2d6b] hover:bg-[#f4f5ff] font-semibold"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                if (isSubmitting) return; // Prevent double-click
                
                setIsSubmitting(true);
                
                const payload = Array.from(selectedKeys)
                  .map((k) => {
                    const r = ranges[k];
                    const row = rowByKey(k);
                    if (!row || !r) return null;

                    return {
                      dockName: row.dockName,
                      dockId: row.dockId,
                      locationRefId: row.locationRefId,
                      calendarRefId: row.calendarRefId,
                      startDate: r.startDate,
                      endDate: r.endDate,
                      startTime: r.startTime,
                      endTime: r.endTime,
                      reason,
                    };
                  })
                  .filter(Boolean);

                console.log("🚀 Submitting bulk close payload:", payload);
                
                await onConfirm?.(payload);
                
                setIsSubmitting(false);
              }}
              disabled={!anySelected || !allSelectedRangesValid || isSubmitting}
              title={
                !anySelected
                  ? "Select at least one dock"
                  : !allSelectedRangesValid
                  ? "Fix date/time for selected rows"
                  : undefined
              }
              className="px-fluid-button-x py-fluid-button-y rounded-full bg-[#c63b2d] text-white hover:opacity-90 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Closing..." : "Close dock(s)"}
            </button>
          </div>
        </div>
        <style>{`
          .bulk-table table { table-layout: fixed; width: 100%; }
          .bulk-table thead th { position: sticky; top: 0; z-index: 1; background: #0e1a4f; color: #fff; }
        `}</style>
      </motion.div>
    </motion.div>
  );
};

export default BulkSelectionModal;
